import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { getLiveTaskBoqBreakdown } from '../../services/api/implementation/impl';

const fmt = (value) =>
  value == null || Number.isNaN(Number(value))
    ? '-'
    : Number(value).toLocaleString('en-IN', { maximumFractionDigits: 2 });

/**
 * For a task created from a BOQ: its work share, how the BOQ worked it out (line items
 * with formula and source drawing), and the materials attached to it by BOM sync.
 */
const TaskBoqBreakdown = ({ taskId }) => {
  const { data, isLoading, isError } = useQuery({
    queryKey: ['liveTaskBoqBreakdown', taskId],
    queryFn: () => getLiveTaskBoqBreakdown(taskId),
    enabled: !!taskId
  });

  if (isLoading) {
    return <p className="text-sm text-gray-500 mb-6">Loading BOQ breakdown…</p>;
  }
  if (isError || !data) {
    return <p className="text-sm text-amber-700 mb-6">Could not load the BOQ breakdown.</p>;
  }

  const { boq, materials = [] } = data;
  const unit = data.unit ?? boq?.items?.[0]?.unit ?? '';

  return (
    <div className="text-sm mb-6 rounded-md border border-gray-200">
      <div className="px-4 py-3 border-b border-gray-200 bg-gray-50">
        <p className="font-semibold">BOQ breakdown</p>
        <p className="text-gray-600 mt-0.5">
          This task: <span className="font-semibold text-gray-900">{fmt(data.quantity)} {unit}</span>
          {boq ? (
            <>
              {' '}
              of {fmt(boq.unitTotal)} {unit} {boq.workTypeName} for the unit
              {boq.sharedByTasks > 1 ? `, shared by ${boq.sharedByTasks} tasks` : ''}
            </>
          ) : null}
        </p>
      </div>

      {boq && !boq.items?.length && (
        <div className="px-4 py-3 border-b border-gray-200 text-gray-600">
          No BOQ lines are assigned to this task. {boq.workTypeName} lines go to the tasks
          that do that work (for example lights and switches to Plates &amp; Switches), floor by
          floor.
        </div>
      )}

      {boq?.items?.length > 0 && (
        <div className="px-4 py-3 border-b border-gray-200">
          <p className="font-semibold mb-2">How it was calculated</p>
          <div className="space-y-2">
            {boq.items.map((item, index) => (
              <div key={`${item.itemName}-${index}`}>
                <p className="text-gray-900">
                  {item.itemName}:{' '}
                  <span className="font-semibold">
                    {fmt(item.taskShare)} {item.unit ?? ''}
                  </span>
                  {boq.sharedByTasks > 1 ? (
                    <span className="text-gray-500">
                      {' '}
                      (unit total {fmt(item.unitTotal)} {item.unit ?? ''})
                    </span>
                  ) : null}
                </p>
                {item.formula ? <p className="text-gray-600">{item.formula}</p> : null}
                {item.source ? <p className="text-xs text-gray-500">Source: {item.source}</p> : null}
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="px-4 py-3">
        <p className="font-semibold mb-2">Materials needed</p>
        {materials.length ? (
          <ul className="space-y-1">
            {materials.map((m, index) => (
              <li key={`${m.name}-${index}`} className="flex justify-between gap-4">
                <span>{m.name}</span>
                <span className="font-semibold whitespace-nowrap">
                  {fmt(m.quantity)} {m.unit ?? ''}
                </span>
              </li>
            ))}
          </ul>
        ) : (
          <p className="text-gray-500">
            No materials attached yet. Use &ldquo;Sync with task&rdquo; on the BOM, or tick
            &ldquo;Attach BOM materials&rdquo; when creating tasks from the BOQ.
          </p>
        )}
      </div>
    </div>
  );
};

export default TaskBoqBreakdown;
